import React, { useState, useMemo, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import DeleteModel from "./DeleteModal"; // ← ADD THIS IMPORT
import PriestApi from "../api/priestApi";

interface AssignTalkModalProps {
  show?: boolean;
  handleClose: () => void;
  allRoasters?: any[];
  talksDataList?: any[];
  retreatRoastApi?: any;
  retreatId?: string | number;
  onAssignSuccess?: () => void;
  spritualDirector?: Record<string, any> | null;
}

function AssignTalkModal({
  show,
  handleClose,
  allRoasters = [],
  talksDataList = [],
  retreatRoastApi,
  retreatId,
  onAssignSuccess,
  spritualDirector,
}: AssignTalkModalProps) {
  const talkOrder = [
    "Holy Matrimony: God, Husband & Wife",
    "The Sacrament of Marriage",
    "Understanding Yourself, Understanding Your Spouse",
    "Communication",
    "Challenges in Marriage",
    "Children and In-Laws",
    "Sex and Marriage",
    "Marital Bliss",
    "Forgiveness",
    "Us vs. the World",
    "Go Forth With God",
  ];
  const [assignments, setAssignments] = useState<any[]>([]);
  const [selectedTalk, setSelectedTalk] = useState<string>("");
  const [selectedRoster, setSelectedRoster] = useState<string>("");
  const [loadingAdd, setLoadingAdd] = useState<boolean>(false);
  const [removingId, setRemovingId] = useState<any>(null);

  // ── ADD THESE TWO STATE VARIABLES ONLY ───────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [pendingRemoveRosterId, setPendingRemoveRosterId] = useState<any>(null);
  // ─────────────────────────────────────────────────────────────────────────

  const priestApi = useMemo(() => new PriestApi(), []);
  const [priestsList, setPriestsList] = useState<any[]>([]);
  const [selectedPriest, setSelectedPriest] = useState<string>("");

  useEffect(() => {
    if (show) {
      const existing = allRoasters
        .filter((r) => r.talkId != null)
        .map((r) => {
          const talk = talksDataList.find(
            (t) => Number(t.talkId) === Number(r.talkId),
          );
          return {
            talkId: Number(r.talkId),
            talkName: talk ? talk.talk : "",
            rosterId: r.id,
            coupleLabel: buildCoupleLabel(r),
            hisEmail: r.hisEmail || null,
            herEmail: r.herEmail || null,
          };
        });
      setAssignments(existing);

      const fetchPriests = async () => {
        try {
          const res: any = await priestApi.getAllPriests({ pageNo: 1, size: 100, export: true });
          if (res.success && res.data) {
            setPriestsList(res.data);
          }
        } catch (err) {
          console.error("Failed to fetch priests", err);
        }
      };
      fetchPriests();
    }
  }, [show, allRoasters, talksDataList]);

  const priestRow = useMemo(() => {
    if (!spritualDirector || !spritualDirector.talk || !spritualDirector.talk.id) return null;
    const talk = spritualDirector.talk;
    return {
      talkId: Number(talk.id),
      talkName: talk.talk,
      rosterId: spritualDirector.rosterId || null,
      coupleLabel: `${spritualDirector.primaryKey ? spritualDirector.primaryKey + " – " : ""}${spritualDirector.title ? spritualDirector.title + " " : ""}${spritualDirector.firstName} ${spritualDirector.lastName || ""}`,
      priestEmail: spritualDirector.email || null,
      isPriest: true,
    };
  }, [spritualDirector]);

  const assignedTalkIds = useMemo(() => {
    const ids = new Set(assignments.map((a) => a.talkId));
    if (priestRow && priestRow.talkId) {
      ids.add(priestRow.talkId);
    }
    return ids;
  }, [assignments, priestRow]);

  const assignedRosterIds = useMemo(
    () => new Set(assignments.map((a) => a.rosterId)),
    [assignments],
  );

  const selectedTalkObj = useMemo(() => {
    return talksDataList.find((t) => String(t.talkId) === String(selectedTalk));
  }, [selectedTalk, talksDataList]);

  const isSacramentSelected = selectedTalkObj && selectedTalkObj.talk === "The Sacrament of Marriage";

  const priestOptions = useMemo(() => {
    return priestsList
      .map((p) => ({
        id: p.id,
        label: `${p.title ? p.title + " " : ""}${p.firstName} ${p.lastName || p.name || ""} - ${p.email || ""}`.trim(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [priestsList]);

  const availableTalks = useMemo(() => {
    return talksDataList
      .filter((t) => !assignedTalkIds.has(Number(t.talkId)))
      .sort((a, b) => {
        const indexA = talkOrder.indexOf(a.talk);
        const indexB = talkOrder.indexOf(b.talk);
        if (indexA === -1 && indexB === -1) return a.talk.localeCompare(b.talk);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
  }, [talksDataList, assignedTalkIds]);

  const availableCouples = useMemo(
    () =>
      allRoasters
        .filter((r) => !assignedRosterIds.has(r.id))
        .map((r) => ({ ...r, label: buildCoupleLabel(r) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allRoasters, assignedRosterIds],
  );

  function buildCoupleLabel(r: Record<string, any>) {
    const pk = r.primaryKey ? `${r.primaryKey} – ` : "";
    const his = [r.hisLastName, r.hisFirstName].filter(Boolean).join(", ");
    const her = r.herFirstName || "";
    return `${pk}${his}${her ? ` & ${her}` : ""}`;
  }

  const handleAdd = async () => {
    if (!selectedTalk || loadingAdd) return;
    if (isSacramentSelected && !selectedRoster && !selectedPriest) return;
    if (!isSacramentSelected && !selectedRoster) return;

    const talk = talksDataList.find(
      (t) => String(t.talkId) === String(selectedTalk),
    );
    if (!talk) return;

    setLoadingAdd(true);
    try {
      let payload = {
        assignments: [],
        retreatId: retreatId,
      };

      if (isSacramentSelected && selectedPriest) {
        payload.assignments.push({
          priestId: Number(selectedPriest),
          talkId: Number(talk.talkId),
        });
      } else {
        const couple = allRoasters.find(
          (r) => String(r.id) === String(selectedRoster),
        );
        if (!couple) {
          setLoadingAdd(false);
          return;
        }
        payload.assignments.push({
          rosterId: couple.id,
          talkId: Number(talk.talkId),
        });
      }

      const res = await retreatRoastApi.updateRoasterTalk(payload);

      if (res.success) {
        setSelectedTalk("");
        setSelectedRoster("");
        setSelectedPriest("");
        if (onAssignSuccess) onAssignSuccess();
      } else {
        alert(res.msg || "Failed to assign talk. Please try again.");
      }
    } catch (err) {
      alert(err?.message || "An error occurred while assigning the talk.");
    } finally {
      setLoadingAdd(false);
    }
  };

  // ── CHANGE 2: Split handleRemove into confirmRemove + handleRemove ────────
  const confirmRemove = (rosterId: string | number) => {
    setPendingRemoveRosterId(rosterId);
    setShowDeleteModal(true);
  };

  const handleRemove = async () => {
    const rosterId = pendingRemoveRosterId;
    setShowDeleteModal(false);
    setPendingRemoveRosterId(null);

    if (!rosterId || removingId === rosterId) return;

    setRemovingId(rosterId);
    try {
      const res = await retreatRoastApi.updateRoasterTalk({
        assignments: [{ rosterId, talkId: null }],
        retreatId: retreatId,
      });

      if (res.success) {
        setAssignments((prev) => prev.filter((a) => a.rosterId !== rosterId));
        if (onAssignSuccess) onAssignSuccess();
      } else {
        alert(res.msg || "Failed to remove talk. Please try again.");
      }
    } catch (err) {
      alert(err?.message || "An error occurred while removing the talk.");
    } finally {
      setRemovingId(null);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  const handleModalClose = () => {
    setSelectedTalk("");
    setSelectedRoster("");
    setSelectedPriest("");
    handleClose();
  };

  const canAdd =
    selectedTalk &&
    (isSacramentSelected ? (selectedRoster || selectedPriest) : selectedRoster) &&
    !loadingAdd;

  return (
    // ── CHANGE 2: Wrap in fragment and add DeleteModel at bottom ─────────
    <>
      <Modal
        show={show}
        onHide={handleModalClose}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton style={styles.header}>
          <Modal.Title style={styles.title}>
            Assign Talks to Couples
          </Modal.Title>
        </Modal.Header>
        <hr />
        <Modal.Body style={styles.body} className="mt-2">
          <div style={styles.selectionCard}>
            <p style={styles.sectionLabel}>
              Select an unassigned talk and a couple, then click{" "}
              <strong>Assign</strong> button.
            </p>

            <div style={styles.dropdownRow}>
              <div style={styles.dropdownWrapper}>
                <label style={styles.dropLabel}>Talk</label>
                <select
                  style={styles.select}
                  value={selectedTalk}
                  onChange={(e) => {
                    setSelectedTalk(e.target.value);
                    setSelectedRoster("");
                    setSelectedPriest("");
                  }}
                  disabled={loadingAdd}
                >
                  <option value="">— Select Talk —</option>
                  {availableTalks.length === 0 && (
                    <option disabled>All talks assigned</option>
                  )}
                  {availableTalks.map((t) => (
                    <option key={t.talkId} value={t.talkId}>
                      {t.talk}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.dropdownWrapper}>
                <label style={styles.dropLabel}>Couple</label>
                <select
                  style={styles.select}
                  value={selectedRoster}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedRoster(val);
                    if (val) {
                      setSelectedPriest("");
                    }
                  }}
                  disabled={loadingAdd}
                >
                  <option value="">— Select Couple —</option>
                  {availableCouples.length === 0 && (
                    <option disabled>All couples assigned</option>
                  )}
                  {availableCouples.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              {isSacramentSelected && (
                <div style={styles.dropdownWrapper}>
                  <label style={styles.dropLabel}>Select Priest</label>
                  <select
                    style={styles.select}
                    value={selectedPriest}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedPriest(val);
                      if (val) {
                        setSelectedRoster("");
                      }
                    }}
                    disabled={loadingAdd}
                  >
                    <option value="">— Select Priest —</option>
                    {priestOptions.length === 0 && (
                      <option disabled>No priests found</option>
                    )}
                    {priestOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <button
                style={{
                  ...styles.addBtn,
                  ...(canAdd ? styles.addBtnActive : styles.addBtnDisabled),
                }}
                onClick={handleAdd}
                disabled={!canAdd}
                title="Assign talk"
              >
                {loadingAdd ? (
                  <span style={styles.spinner} />
                ) : (
                  <span style={styles.addBtnIcon}>＋</span>
                )}
                <span>{loadingAdd ? "Saving..." : "Assign"}</span>
              </button>
            </div>
          </div>

          <div style={styles.divider} />

          <div>
            <p style={styles.assignedHeader}>
              Assigned Talks
              <span style={styles.badge}>
                {assignments.length + (priestRow ? 1 : 0)}
              </span>
            </p>

            {assignments.length === 0 && !priestRow ? (
              <div style={styles.emptyState}>
                <span style={styles.emptyIcon}>📋</span>
                <p style={styles.emptyText}>
                  No talks assigned yet. Use the selectors above to get started.
                </p>
              </div>
            ) : (
              <div style={styles.assignedList}>
                <div
                  style={{ ...styles.assignedRow, ...styles.assignedRowHeader }}
                >
                  <span style={{ ...styles.col, ...styles.colTalk }}>Talk</span>
                  <span style={{ ...styles.col, ...styles.colCouple }}>
                    Couple/Priest
                  </span>
                  <span style={{ ...styles.col, ...styles.colAction }}>
                    Action
                  </span>
                </div>

                {priestRow && (
                  <div style={{ ...styles.assignedRow, ...styles.rowEven }}>
                    <span style={{ ...styles.col, ...styles.colTalk }}>
                      <span style={styles.talkPillPriest}>
                        {priestRow.talkName}
                      </span>
                    </span>
                    <span style={{ ...styles.col, ...styles.colCouple }}>
                      <span style={{ fontWeight: 600 }}>
                        {priestRow.coupleLabel}
                      </span>
                      {priestRow.priestEmail && (
                        <span style={styles.emailText}>
                          ({priestRow.priestEmail})
                        </span>
                      )}
                      <span style={styles.priestBadge}>Spiritual Director</span>
                    </span>
                    <span style={{ ...styles.col, ...styles.colAction }}>
                      {priestRow.talkId ? (
                        <button
                          className="font-size"
                          onClick={() => confirmRemove(priestRow.rosterId)}
                          disabled={removingId === priestRow.rosterId || loadingAdd}
                        >
                          <i
                            className={`mdi mdi-delete-forever text-md  mx-2 deleteRetreatButton font-size`}
                            title="remove Assigned talk"
                          ></i>
                        </button>
                      ) : (
                        <span
                          style={{
                            fontSize: "0.75rem",
                            color: "#aaa",
                            marginRight: "23px",
                          }}
                        >
                          —
                        </span>
                      )}
                    </span>
                  </div>
                )}

                {assignments.map((a, i) => {
                  const isRemoving = removingId === a.rosterId;
                  return (
                    <div
                      key={a.rosterId}
                      style={{
                        ...styles.assignedRow,
                        ...(i % 2 === 0 ? styles.rowOdd : styles.rowEven),
                        opacity: isRemoving ? 0.45 : 1,
                        transition: "opacity 0.2s",
                      }}
                    >
                      <span style={{ ...styles.col, ...styles.colTalk }}>
                        <span style={styles.talkPill}>{a.talkName}</span>
                      </span>
                      <span style={{ ...styles.col, ...styles.colCouple }}>
                        <span>{a.coupleLabel}</span>
                        {(a.hisEmail || a.herEmail) && (
                          <span style={styles.emailText}>
                            (
                            {[a.hisEmail, a.herEmail]
                              .filter(Boolean)
                              .join(" & ")}
                            )
                          </span>
                        )}
                      </span>
                      <span style={{ ...styles.col, ...styles.colAction }}>
                        {/* ── CHANGE 2: onClick → confirmRemove ── */}
                        <button
                          className="font-size"
                          onClick={() => confirmRemove(a.rosterId)}
                          disabled={isRemoving || loadingAdd}
                        >
                          <i
                            className={`mdi mdi-delete-forever text-md  mx-2 deleteRetreatButton font-size`}
                            title="remove Assigned talk"
                          ></i>
                        </button>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer style={styles.footer}>
          <Button variant="secondary" onClick={handleModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── CHANGE 2: Delete confirmation modal ── */}
      <DeleteModel
        show={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setPendingRemoveRosterId(null);
        }}
        DeleteData={handleRemove}
        content={
          <>
            <h5 className="mt-4 fw-bold">Talk Assignment</h5>
            <div className="p-3 border rounded-3 bg-light shadow-sm">
              {pendingRemoveRosterId &&
                (() => {
                  let assignment = assignments.find(
                    (a) => a.rosterId === pendingRemoveRosterId,
                  );
                  if (!assignment && priestRow && priestRow.rosterId === pendingRemoveRosterId) {
                     assignment = {
                       talkName: priestRow.talkName,
                       coupleLabel: priestRow.coupleLabel,
                     };
                  }
                  return assignment ? (
                    <>
                      <div className="mb-2">
                        <span className="fw-bold">Talk:</span>{" "}
                        <span className="text-muted">
                          {assignment.talkName}
                        </span>
                      </div>
                      <div className="mb-2">
                        <span className="fw-bold">Assignee:</span>{" "}
                        <span className="text-muted">
                          {assignment.coupleLabel}
                        </span>
                      </div>
                    </>
                  ) : null;
                })()}
            </div>
          </>
        }
      />
    </>
  );
}

// ── Inline styles (100% unchanged) ───────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  header: {
    borderBottom: "none",
    padding: "18px 24px",
  },
  title: {
    fontWeight: 700,
    fontSize: "1.15rem",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "-17px",
  },
  titleIcon: { fontSize: "1.3rem" },
  body: {
    padding: "24px",
    backgroundColor: "#f8f9fc",
    minHeight: "300px",
  },
  selectionCard: {
    background: "#fff",
    borderRadius: "10px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    marginBottom: "20px",
  },
  sectionLabel: {
    fontSize: "0.85rem",
    color: "#666",
    marginBottom: "16px",
  },
  dropdownRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: "14px",
    flexWrap: "wrap",
  },
  dropdownWrapper: {
    flex: 1,
    minWidth: "200px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  dropLabel: {
    fontWeight: 600,
    fontSize: "0.82rem",
    color: "#444",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  select: {
    width: "100%",
    padding: "9px 12px",
    borderRadius: "7px",
    border: "1.5px solid #d0d7e3",
    fontSize: "0.88rem",
    background: "#fff",
    color: "#222",
    outline: "none",
    cursor: "pointer",
  },
  addBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "9px 20px",
    borderRadius: "7px",
    border: "none",
    fontWeight: 700,
    fontSize: "0.88rem",
    cursor: "pointer",
    transition: "all 0.2s",
    whiteSpace: "nowrap",
    alignSelf: "flex-end",
    minWidth: "105px",
    justifyContent: "center",
  },
  addBtnActive: {
    background: "linear-gradient(135deg, #1a73e8, #0d47a1)",
    color: "#fff",
    boxShadow: "0 3px 10px rgba(26,115,232,0.35)",
  },
  addBtnDisabled: {
    background: "#e0e0e0",
    color: "#aaa",
    cursor: "not-allowed",
  },
  addBtnIcon: { fontSize: "1.1rem", lineHeight: 1 },
  spinner: {
    display: "inline-block",
    width: "14px",
    height: "14px",
    border: "2px solid rgba(255,255,255,0.4)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "assignTalkSpin 0.7s linear infinite",
  },
  divider: {
    height: "1px",
    background: "linear-gradient(to right, transparent, #d0d7e3, transparent)",
    margin: "4px 0 20px",
  },
  assignedHeader: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: "#222",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
  },
  badge: {
    background: "#1a73e8",
    color: "#fff",
    borderRadius: "12px",
    padding: "2px 10px",
    fontSize: "0.78rem",
    fontWeight: 700,
  },
  assignedList: {
    borderRadius: "10px",
    overflow: "hidden",
    border: "1px solid #e0e7f0",
    background: "#fff",
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  },
  assignedRow: {
    display: "flex",
    alignItems: "center",
    padding: "10px 16px",
  },
  assignedRowHeader: {
    background: "#e8eef7",
    fontWeight: 700,
    fontSize: "0.78rem",
    letterSpacing: "0.6px",
    color: "#5c6b82",
    borderBottom: "1px solid #d0daea",
  },
  rowEven: { background: "#fff" },
  rowOdd: { background: "#f7f9fc" },
  col: { fontSize: "0.88rem", color: "#333" },
  colTalk: { flex: "0 0 38%", paddingRight: "12px" },
  colCouple: {
    flex: "1",
    paddingRight: "12px",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px",
  },
  colAction: { flex: "0 0 120px", textAlign: "right" },
  talkPill: {
    display: "inline-block",
    background: "#e3edff",
    color: "#1a73e8",
    borderRadius: "20px",
    padding: "3px 12px",
    fontSize: "0.82rem",
    fontWeight: 600,
  },
  talkPillPriest: {
    display: "inline-block",
    background: "#fff3e0",
    color: "#e65100",
    borderRadius: "20px",
    padding: "3px 12px",
    fontSize: "0.82rem",
    fontWeight: 600,
  },
  priestBadge: {
    fontSize: "0.75rem",
    background: "#e8f5e9",
    color: "#2e7d32",
    borderRadius: "10px",
    padding: "2px 8px",
    fontWeight: 600,
  },
  emailText: {
    fontSize: "0.78rem",
    color: "#888",
    fontStyle: "italic",
  },
  removeBtn: {
    background: "transparent",
    border: "1.5px solid #e53935",
    color: "#e53935",
    borderRadius: "6px",
    padding: "4px 10px",
    fontSize: "0.78rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.18s",
  },
  removeBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    background: "#fff",
    borderRadius: "10px",
    border: "1px dashed #c8d4e8",
  },
  emptyIcon: { fontSize: "2rem", display: "block", marginBottom: "10px" },
  emptyText: { color: "#888", fontSize: "0.88rem", margin: 0 },
  footer: {
    borderTop: "1px solid #e8eef7",
    background: "#fff",
    padding: "14px 24px",
  },
};

if (
  typeof document !== "undefined" &&
  !document.getElementById("assign-talk-spin-style")
) {
  const styleTag = document.createElement("style");
  styleTag.id = "assign-talk-spin-style";
  styleTag.innerHTML = `@keyframes assignTalkSpin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(styleTag);
}

export default AssignTalkModal;
